import { useState, useEffect, useCallback } from "react";

export interface BrandSettings {
  siteName: string;
  slogan: string;
  logoUrl: string;
  logoDarkUrl: string;
  faviconUrl: string;
  primaryColor: string;
  accentColor: string;
}

const defaultSettings: BrandSettings = {
  siteName: "Helvetik Journal",
  slogan: "Die Schweiz im Fokus.",
  logoUrl: "",
  logoDarkUrl: "",
  faviconUrl: "",
  primaryColor: "#0F172A",
  accentColor: "#8B0000",
};

const STORAGE_KEY = "hj_brand_settings";

function loadSettings(): BrandSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...defaultSettings, ...parsed };
    }
  } catch {
    // ignore parse errors
  }
  return { ...defaultSettings };
}

function saveSettings(settings: BrandSettings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function useBrandSettings() {
  const [settings, setSettingsState] = useState<BrandSettings>(loadSettings);

  const setSettings = useCallback((newSettings: Partial<BrandSettings>) => {
    setSettingsState((prev) => {
      const updated = { ...prev, ...newSettings };
      saveSettings(updated);
      return updated;
    });
  }, []);

  const resetSettings = useCallback(() => {
    setSettingsState({ ...defaultSettings });
    saveSettings({ ...defaultSettings });
  }, []);

  // Sync across tabs
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setSettingsState(loadSettings());
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return { settings, setSettings, resetSettings };
}

export function getBrandSettings(): BrandSettings {
  return loadSettings();
}